import { useEffect, useRef, useState } from "react";
import { type LucideIcon, Inbox, QrCode, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import {
  useAccessRequests,
  useAgentUsers,
  useDeleteAgentUser,
  useInviteAgentUsers,
  useResolveAccessRequest,
  useUpdateAgentUser,
} from "@/hooks/agent/useAgentPage";
import { useTeamRecipients } from "@/hooks/activation";
import {
  clearEmailCampaign,
  type EmailCampaignIdentity,
  getOrCreateEmailCampaign,
  loadPendingEmailCampaign,
} from "@/services/activation/emailCampaignId";
import AccessRequestsList from "../invite/AccessRequestsList";
import AgentUsersList from "../invite/AgentUsersList";
import ScanActivityList from "../invite/ScanActivityList";

interface PackInviteSectionProps {
  agentId: string;
  brand?: string;
}

type Tab = "requests" | "users" | "scans";

const TABS: { id: Tab; label: string; Icon: LucideIcon }[] = [
  { id: "requests", label: "Requests", Icon: Inbox },
  { id: "users", label: "People with access", Icon: Users },
  { id: "scans", label: "Scan activity", Icon: QrCode },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_INVITES_PER_BATCH = 50;

export const PackInviteSection = ({
  agentId,
  brand = "#4E9CFF",
}: PackInviteSectionProps) => {
  const { user } = useAuth();
  const [emailInput, setEmailInput] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("requests");
  const [editingExpiry, setEditingExpiry] = useState<Record<string, string>>(
    {},
  );
  const [prefilled, setPrefilled] = useState(false);
  const [lastResult, setLastResult] = useState<{
    unknown: number;
    notSent: number;
  } | null>(null);
  const [isRestoringCampaign, setIsRestoringCampaign] = useState(true);
  const campaignRef = useRef<EmailCampaignIdentity | null>(null);
  const campaignScope = `genie-access-invite:${agentId}:never`;

  const inviteUsers = useInviteAgentUsers(agentId);
  const { data: users = [], isLoading: usersLoading } = useAgentUsers(agentId);
  const { data: requests = [], isLoading: requestsLoading } =
    useAccessRequests(agentId);
  const deleteUser = useDeleteAgentUser(agentId);
  const updateUser = useUpdateAgentUser(agentId);
  const resolveRequest = useResolveAccessRequest(agentId);
  const { members: teamMembers } = useTeamRecipients();

  useEffect(() => {
    let active = true;
    campaignRef.current = null;
    setLastResult(null);
    setIsRestoringCampaign(true);
    void loadPendingEmailCampaign(campaignScope)
      .then((campaign) => {
        if (!active || !campaign) return;
        campaignRef.current = campaign;
        setEmailInput(campaign.recipients.join(", "));
        setPrefilled(true);
      })
      .finally(() => {
        if (active) setIsRestoringCampaign(false);
      });
    return () => {
      active = false;
    };
  }, [campaignScope]);

  // Default the recipient box to teammates who don't already have access —
  // the common case becomes "review, hit Send" instead of typing emails.
  useEffect(() => {
    if (prefilled || usersLoading || teamMembers.length === 0) return;
    const existingEmails = new Set(
      (users as Array<{ profile?: { email?: string } }>)
        .map((u) => u.profile?.email?.toLowerCase())
        .filter(Boolean),
    );
    const candidates = teamMembers
      .map((member) => member.email!.toLowerCase())
      .filter((email) => !existingEmails.has(email));
    if (candidates.length > 0) setEmailInput(candidates.join(", "));
    setPrefilled(true);
  }, [prefilled, usersLoading, teamMembers, users]);

  const pendingCount = (requests as any[]).filter((r) => !r.resolved_at).length;

  const parsedEmails = [
    ...new Set(
      emailInput
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  const validEmails = parsedEmails.filter((e) => EMAIL_PATTERN.test(e));
  const invalidCount = parsedEmails.length - validEmails.length;
  const exceedsInviteLimit = validEmails.length > MAX_INVITES_PER_BATCH;

  const handleSendInvites = async () => {
    if (validEmails.length === 0 || exceedsInviteLimit) return;
    const fingerprint = [campaignScope, ...validEmails.sort()].join("\n");
    if (
      campaignRef.current &&
      campaignRef.current.fingerprint !== fingerprint
    ) {
      const replace = globalThis.confirm(
        "A previous invitation campaign is still pending. Replacing it may duplicate recipients whose delivery could not be confirmed. Start a new campaign with this edited list?",
      );
      if (!replace) return;
      clearEmailCampaign(campaignRef.current);
      campaignRef.current = null;
    }
    if (campaignRef.current?.fingerprint !== fingerprint) {
      campaignRef.current = await getOrCreateEmailCampaign(
        campaignScope,
        validEmails,
      );
    }
    const campaign = campaignRef.current;
    inviteUsers.mutate(
      { emails: validEmails, campaignId: campaign.id },
      {
        onSuccess: (results) => {
          const sent = results.filter((r) => r.sent).length;
          const unknown = results.filter((r) => r.state === "unknown").length;
          const notSent = results.filter((r) => r.state === "not_sent").length;
          setLastResult({ unknown, notSent });
          if (unknown > 0) {
            toast.warning(
              `${sent} sent; ${unknown} unknown and locked against duplicate invites`,
            );
          } else if (notSent > 0) {
            toast.error(
              `${notSent} known not sent. Retry safely to send only those invites.`,
            );
          } else {
            toast.success(sent === 1 ? "Invite sent" : `${sent} invites sent`);
            setEmailInput("");
            clearEmailCampaign(campaign);
            if (campaignRef.current?.id === campaign.id)
              campaignRef.current = null;
            setLastResult(null);
          }
        },
        onError: () =>
          toast.error(
            "Couldn't confirm the invites. Retry safely to resume them.",
          ),
      },
    );
  };

  const handleStartNewCampaign = () => {
    if (
      lastResult?.unknown &&
      !globalThis.confirm(
        "Some invite results are unknown. Starting a new send may create duplicate invitations. Continue?",
      )
    )
      return;
    if (campaignRef.current) clearEmailCampaign(campaignRef.current);
    campaignRef.current = null;
    setLastResult(null);
    toast.info("Ready to start a new invitation campaign");
  };

  const handleExpiryEdit = (userId: string, current: string | null) => {
    setEditingExpiry((prev) => ({
      ...prev,
      [userId]: current ? current.split("T")[0] : "",
    }));
  };

  const handleExpirySave = (userId: string) => {
    const raw = editingExpiry[userId];
    updateUser.mutate(
      { userId, expiresAt: raw ? new Date(raw).toISOString() : null },
      {
        onSuccess: () => {
          toast.success("Expiry updated");
          setEditingExpiry((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
          });
        },
        onError: () => toast.error("Failed to update expiry"),
      },
    );
  };

  const handleExpiryCancel = (userId: string) => {
    setEditingExpiry((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const handleRemoveUser = (userId: string) => {
    deleteUser.mutate(userId, {
      onSuccess: () => toast.success("Access revoked"),
      onError: () => toast.error("Failed to revoke access"),
    });
  };

  const handleResolve = (
    requestId: number,
    solution: "approved" | "denied",
  ) => {
    resolveRequest.mutate(
      { requestId, solution },
      {
        onSuccess: (result) => {
          if (result.deliveryState === "unknown") {
            toast.warning(
              result.warning ||
                "Request approved, but email delivery could not be confirmed",
            );
            return;
          }
          toast.success(
            solution === "approved" ? "Request approved" : "Request denied",
          );
        },
        onError: () => toast.error("Failed to resolve request"),
      },
    );
  };

  return (
    <OverviewSectionCard
      title="Give people login access"
      subtitle="Grant a named HelpGenie login so someone can view and manage this Genie in the dashboard — not for customers, who don't need to sign in."
      genieIcon="lock"
      brand={brand}
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <Textarea
          value={emailInput}
          disabled={isRestoringCampaign}
          onChange={(e) => {
            setEmailInput(e.target.value);
            setLastResult(null);
          }}
          placeholder="name@company.com, another@company.com"
          className="min-h-[44px] resize-none"
          rows={1}
        />
        <Button
          onClick={handleSendInvites}
          disabled={
            validEmails.length === 0 ||
            exceedsInviteLimit ||
            isRestoringCampaign ||
            inviteUsers.isPending
          }
          className="text-white border-0 shrink-0"
          style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
        >
          <Send className="w-4 h-4 mr-2" />
          {isRestoringCampaign
            ? "Restoring…"
            : inviteUsers.isPending
              ? "Sending…"
              : `Send${validEmails.length ? ` (${validEmails.length})` : ""}`}
        </Button>
      </div>
      {invalidCount > 0 && (
        <p className="text-xs text-red-500 mt-1.5">
          {invalidCount} entry{invalidCount === 1 ? "" : "ies"} isn't a valid
          email and will be skipped
        </p>
      )}
      {exceedsInviteLimit && (
        <p className="text-xs text-red-500 mt-1.5">
          Invite at most {MAX_INVITES_PER_BATCH} people at a time.
        </p>
      )}
      {lastResult?.unknown ? (
        <p className="text-xs text-amber-700 mt-1.5">
          Unknown deliveries stay locked against retries.{" "}
          <button
            type="button"
            className="underline"
            onClick={handleStartNewCampaign}
          >
            Start a new campaign (may duplicate)
          </button>
        </p>
      ) : null}
      {invalidCount === 0 && prefilled && validEmails.length > 0 && (
        <p className="text-xs text-gray-500 mt-1.5">
          Prefilled from your team — edit or just hit send
        </p>
      )}

      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="p-[2px] rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] inline-flex mb-4">
          <div className="flex rounded-[10px] bg-white">
            {TABS.map((tab, i) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 transition-colors flex items-center gap-1.5 text-sm ${
                  i === 0
                    ? "rounded-l-lg"
                    : i === TABS.length - 1
                      ? "rounded-r-lg"
                      : ""
                } ${
                  activeTab === tab.id
                    ? "bg-gray-100 text-gray-900 font-medium"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <tab.Icon className="h-3.5 w-3.5" />
                {tab.label}
                {tab.id === "requests" && pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white text-[10px] font-semibold">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "requests" && (
          <AccessRequestsList
            requests={requests as any[]}
            isLoading={requestsLoading}
            isPending={resolveRequest.isPending}
            onResolve={handleResolve}
          />
        )}
        {activeTab === "users" && (
          <AgentUsersList
            users={users as any[]}
            isLoading={usersLoading}
            authUserId={user?.id}
            editingExpiry={editingExpiry}
            isUpdatePending={updateUser.isPending}
            isDeletePending={deleteUser.isPending}
            onExpiryEdit={handleExpiryEdit}
            onExpirySave={handleExpirySave}
            onExpiryCancel={handleExpiryCancel}
            onExpiryChange={(userId, value) =>
              setEditingExpiry((prev) => ({ ...prev, [userId]: value }))
            }
            onRemove={handleRemoveUser}
          />
        )}
        {activeTab === "scans" && <ScanActivityList agentId={agentId} brand={brand} />}
      </div>
    </OverviewSectionCard>
  );
};
